import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { DateRangePicker } from 'react-dates';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { useQuery } from 'urql';
import { assets } from '../../../../assets';
import Button from '../../../../components/buttons/button';
import { BlurredContainerM3030 } from '../../../../components/containers/containers';
import DataRow from '../../../../components/data-viewers/data-row/data-row';
import ButtonSelect, { ButtonSelectOption } from '../../../../components/inputs/button-select';
import StyledCheckbox from '../../../../components/inputs/styled-checkbox';
import StyledInput from '../../../../components/inputs/styled-input';
import StyledReactSelect from '../../../../components/inputs/styled-react-select';
import ErrorShower from '../../../../components/ui-utils/error-shower';
import { InfoIcon } from '../../../../components/ui-utils/info-icon';
import { formatNumber } from '../../../../helpers/number-formatters';
import { GraphqlOperations } from '../../../../logic/graphql/operations';
import {
  Account,
  DaytradeType,
  Direction,
  ExchangeRateType,
  Query,
  Security,
  SecurityListType,
  SecurityType,
} from '../../../../logic/graphql/types/types.d';
import ExchangeOrders from '../../../../logic/redux/exchange-orders';
import { EXCHANGE_ORDER_MAX_INTERVAL } from '../../../../logic/redux/exchange-orders/constants';
import { diffInDays, isDateInDates } from '../../../../logic/redux/exchange-orders/helpers';
import Presentation from '../../../../logic/redux/presentation';
import { Paths } from '../../../../navigation/paths';
import ExchangeOrderFieldInfo from '../components/exchange-order-field-info/exchange-order-field-info';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';

const FormContainer = styled.div`
  width: 100%;
  margin-bottom: 40px;
  @media (min-width: 768px) {
    width: 50%;
  }
`;

const FormBlockContainer = styled.section`
  width: 100%;
  margin-bottom: 40px;
`;

type InfoType = 'validity' | 'stock-market-time' | 'type' | 'daytrade';

const getSecurityListType = (direction?: Direction, isDaytrade?: boolean): SecurityListType => {
  if (direction === Direction.Sell && !!isDaytrade) {
    return SecurityListType.All;
  } else if (direction === Direction.Sell) {
    return SecurityListType.OwnActual;
  }
  return SecurityListType.All;
};

const getExchangeTypeOptions = (
  intl: IntlShape,
  direction?: Direction,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  security?: Security,
): { label: string; value: ExchangeRateType }[] => {
  const typeOptions: { label: string; value: ExchangeRateType }[] = [
    {
      label: intl.formatMessage({ id: 'order.exchangeRateType.limit_price' }),
      value: ExchangeRateType.LimitPrice,
    },
  ];
  if (direction === Direction.Sell) {
    typeOptions.push({
      label: intl.formatMessage({ id: 'order.exchangeRateType.stop_market' }),
      value: ExchangeRateType.StopMarket,
    });
  }

  typeOptions.push({
    label: intl.formatMessage({ id: 'order.exchangeRateType.stop_limit' }),
    value: ExchangeRateType.StopLimit,
  });

  return typeOptions;
};

const NewExchangeOrderForm = (props: { onFinishedEditing: () => void }): JSX.Element => {
  // MARK: - Resources
  const history = useHistory();
  const dispatch = useDispatch();
  const intl = useIntl();

  const exchangeOrder = useSelector(ExchangeOrders.select.selectEditedExchangeOrder);
  const isValidating = useSelector(ExchangeOrders.select.selectShouldValidate);
  const validationErrors = useSelector(ExchangeOrders.select.selectExchangeOrderValidationErrors);
  const isValid = useSelector(ExchangeOrders.select.selectExchangeOrderIsValid);
  const currentValidDays = useSelector(ExchangeOrders.select.selectValidDates);
  const isMobile = useSelector(Presentation.select.selectIsMobileSize);
  const { selectableStartDates, selectableEndDates } = useSelector(
    ExchangeOrders.select.selectSelectableDates,
  );

  // MARK: - State
  const [openInfo, setOpenInfo] = useState<InfoType | undefined>(undefined);
  const [datePickerFocusedInput, setDatePickerFocusedInput] = useState<
    'startDate' | 'endDate' | null
  >(null);
  const selectableDays =
    datePickerFocusedInput === 'startDate'
      ? selectableStartDates
      : datePickerFocusedInput === 'endDate'
      ? selectableEndDates
      : [];
  // MARK: - Graphql operations

  const userAccountsQueryResult = useQuery<Query>({
    query: GraphqlOperations.queries.getUserAccounts.queryString,
  })[0];

  const getAccountBalanceQueryResult = useQuery<Query>({
    query: GraphqlOperations.queries.get_account_balance.queryString,
    variables: GraphqlOperations.queries.get_account_balance.createVars(
      exchangeOrder?.account || '',
      exchangeOrder?.securityDetails?.currency?.currency || '',
    ),
    pause: !exchangeOrder?.account || !exchangeOrder?.securityDetails?.currency?.currency,
    requestPolicy: 'network-only',
  })[0];

  useEffect(() => {
    if (getAccountBalanceQueryResult.fetching) {
      dispatch(ExchangeOrders.actions.setInvestableAccountBalance(undefined));
      return;
    }
    const investableBalance = getAccountBalanceQueryResult.data?.get_account_balance?.investable;
    dispatch(ExchangeOrders.actions.setInvestableAccountBalance(investableBalance));
  }, [getAccountBalanceQueryResult.data, getAccountBalanceQueryResult.fetching, dispatch]);

  const getSecurityAmountQueryResult = useQuery<Query>({
    query: GraphqlOperations.queries.get_security_amount.queryString,
    variables: GraphqlOperations.queries.get_security_amount.createVars(
      exchangeOrder?.account || '',
      exchangeOrder?.security || '',
    ),
    pause: !exchangeOrder?.account || !exchangeOrder?.security,
    requestPolicy: 'network-only',
  })[0];

  useEffect(() => {
    if (getSecurityAmountQueryResult.fetching) {
      dispatch(ExchangeOrders.actions.setInvestableSecurityAmount(undefined));
      return;
    }
    const investableBalance = getSecurityAmountQueryResult.data?.get_security_amount?.investable;
    dispatch(ExchangeOrders.actions.setInvestableSecurityAmount(investableBalance));
  }, [getSecurityAmountQueryResult.data, getSecurityAmountQueryResult.fetching, dispatch]);

  const getSecuritiesQueryResult = useQuery<Query>({
    query: GraphqlOperations.queries.get_securities.queryString,
    variables: GraphqlOperations.queries.get_securities.createVars(
      SecurityType.ExchangeOrder,
      getSecurityListType(exchangeOrder?.direction, exchangeOrder?.daytrade),
      exchangeOrder?.account || '',
    ),
    pause: !exchangeOrder?.account || !exchangeOrder?.direction,
    requestPolicy: 'network-only',
  })[0];

  const getValidTradingDaysQueryResult = useQuery<Query>({
    query: GraphqlOperations.queries.get_valid_trading_days.queryString,
    requestPolicy: 'network-only',
  })[0];
  const allValidDays: string[] = getValidTradingDaysQueryResult.data?.get_valid_trading_days || [];

  // MARK: - Lifecycle methods

  useEffect(() => {
    if (currentValidDays.length === 0 && allValidDays.length > 0) {
      dispatch(ExchangeOrders.actions.setValidDates(allValidDays));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allValidDays]);

  useEffect(() => {
    return () => {
      dispatch(ExchangeOrders.actions.setValidDates([]));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // emptying invalid security if it is not in the list
    if (exchangeOrder?.security) {
      const selectableSecurities = getSecuritiesQueryResult.data?.get_securities;
      if (selectableSecurities !== undefined) {
        // act when have data
        const contains = !!selectableSecurities.find(
          (sec) => sec.short_name === exchangeOrder.security,
        );
        if (!contains) {
          dispatch(
            ExchangeOrders.actions.setEditedExchangeOrder({
              ...exchangeOrder,
              security: undefined,
              securityDetails: undefined,
            }),
          );
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getSecuritiesQueryResult.data]);

  // MARK: - Event handlers

  const handleNext1Click = (): void => {
    if (!isValid) {
      if (!isValidating) {
        dispatch(ExchangeOrders.actions.setShouldValidate(true));
      }
      return;
    }

    props.onFinishedEditing();
  };

  const handleDirectionChange = (option: ButtonSelectOption): void => {
    // stop piaci csak eladási műveletnél
    if (
      (option.value as Direction) === Direction.Buy &&
      exchangeOrder?.type === ExchangeRateType.StopMarket
    ) {
      dispatch(
        ExchangeOrders.actions.setEditedExchangeOrder({
          ...exchangeOrder,
          direction: option.value as Direction,
          type: undefined,
        }),
      );
    } else {
      dispatch(
        ExchangeOrders.actions.setEditedExchangeOrder({
          ...exchangeOrder,
          direction: option.value as Direction,
        }),
      );
    }
  };
  const handleAccountChange = (value: string | number | number[] | string[]): void => {
    dispatch(
      ExchangeOrders.actions.setEditedExchangeOrder({
        ...exchangeOrder,
        account: value as string,
        accountDetails: userAccountsQueryResult.data?.getUser?.accounts?.find(
          (a) => a.number === value,
        ),
      }),
    );
  };
  const handleSecurityChange = (value: string | number | number[] | string[]): void => {
    const security = getSecuritiesQueryResult.data?.get_securities.find(
      (s) => s.short_name === value,
    );
    dispatch(
      ExchangeOrders.actions.setEditedExchangeOrder({
        ...exchangeOrder,
        security: value as string,
        securityDetails: security,
      }),
    );
  };

  const handleDaytradeChange = (value: boolean): void => {
    if (value) {
      const today = new Date().toISOString();
      dispatch(
        ExchangeOrders.actions.setEditedExchangeOrder({
          ...exchangeOrder,
          daytrade: value,
          validity_start: today, // daytradeValidDay,
          validity_end: today, // daytradeValidDay,
        }),
      );
    } else {
      dispatch(
        ExchangeOrders.actions.setEditedExchangeOrder({
          ...exchangeOrder,
          daytrade: value,
          validity_start: undefined,
          validity_end: undefined,
          daytrade_type: undefined,
        }),
      );
    }
  };

  const handleDaytradeTypeChange = (option: ButtonSelectOption): void => {
    dispatch(
      ExchangeOrders.actions.setEditedExchangeOrder({
        ...exchangeOrder,
        daytrade_type: option.value as DaytradeType,
      }),
    );
  };

  const handleExchangeTypeChange = (option: ButtonSelectOption): void => {
    const type = option.value as ExchangeRateType;
    switch (type) {
      case ExchangeRateType.LimitPrice:
        dispatch(
          ExchangeOrders.actions.setEditedExchangeOrder({
            ...exchangeOrder,
            type,
            activation_price: undefined,
          }),
        );
        break;
      case ExchangeRateType.StopLimit:
        dispatch(
          ExchangeOrders.actions.setEditedExchangeOrder({
            ...exchangeOrder,
            type,
          }),
        );
        break;
      case ExchangeRateType.StopMarket:
        dispatch(
          ExchangeOrders.actions.setEditedExchangeOrder({
            ...exchangeOrder,
            type,
            limit_price: undefined,
          }),
        );
        break;
    }
  };

  const handleCountChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const count = parseInt(event.currentTarget.value);
    dispatch(
      ExchangeOrders.actions.setEditedExchangeOrder({
        ...exchangeOrder,
        count: Math.abs(count),
      }),
    );
  };

  const handleActivationPriceChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const parsedActivationPrice = parseFloat(event.currentTarget.value);
    const activation_price =
      parsedActivationPrice || parsedActivationPrice === 0
        ? Math.abs(parsedActivationPrice)
        : undefined;
    dispatch(
      ExchangeOrders.actions.setEditedExchangeOrder({
        ...exchangeOrder,
        activation_price,
      }),
    );
  };

  const handleLimitPriceChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const parsedLimitPrice = parseFloat(event.currentTarget.value);
    const limit_price =
      parsedLimitPrice || parsedLimitPrice === 0 ? Math.abs(parsedLimitPrice) : undefined;
    dispatch(
      ExchangeOrders.actions.setEditedExchangeOrder({
        ...exchangeOrder,
        limit_price,
      }),
    );
  };

  const handleOutsideTradingHoursChange = (value: boolean): void => {
    dispatch(
      ExchangeOrders.actions.setEditedExchangeOrder({
        ...exchangeOrder,
        outside_trading_hours: value,
      }),
    );
  };

  const handleDatesChange = ({
    startDate,
    endDate,
  }: {
    startDate: moment.Moment | null;
    endDate: moment.Moment | null;
  }): void => {
    if (startDate && endDate && diffInDays(startDate, endDate) >= EXCHANGE_ORDER_MAX_INTERVAL) {
      const validity_start = startDate?.toISOString();
      dispatch(
        ExchangeOrders.actions.setEditedExchangeOrder({
          ...exchangeOrder,
          validity_start,
          validity_end: undefined,
        }),
      );
    } else {
      const validity_start = startDate?.toISOString();
      const validity_end = endDate?.toISOString();
      dispatch(
        ExchangeOrders.actions.setEditedExchangeOrder({
          ...exchangeOrder,
          validity_start,
          validity_end,
        }),
      );
    }
  };

  // MARK: - Form subparts

  const directionSelect = (
    <ButtonSelect
      options={[
        { label: intl.formatMessage({ id: 'order.direction.buy' }), value: Direction.Buy },
        { label: intl.formatMessage({ id: 'order.direction.sell' }), value: Direction.Sell },
      ]}
      onSelect={handleDirectionChange}
      className="mb-3"
      initialValue={exchangeOrder?.direction}
      error={validationErrors.direction && intl.formatMessage({ id: validationErrors.direction })}
    />
  );

  const accountSelect = (
    <StyledReactSelect
      placeholder={intl.formatMessage({
        id: 'exchangeOrderFormPage.form.accountSelectPlaceholder',
      })}
      options={[
        ...(userAccountsQueryResult.data?.getUser?.accounts.map((account: Account) => ({
          value: account.number,
          label: `${account.type}: ${account.number}`,
        })) || []),
      ]}
      isLoading={userAccountsQueryResult.fetching}
      onChange={handleAccountChange}
      defaultValue={
        exchangeOrder?.accountDetails
          ? {
              value: exchangeOrder.accountDetails.number,
              label: `${exchangeOrder.accountDetails.type}: ${exchangeOrder.accountDetails.number}`,
            }
          : undefined
      }
      disabled={!exchangeOrder?.direction}
      error={validationErrors.account && intl.formatMessage({ id: validationErrors.account })}
    />
  );

  const observedBalance =
    exchangeOrder?.direction === Direction.Buy
      ? getAccountBalanceQueryResult?.data?.get_account_balance
      : getSecurityAmountQueryResult?.data?.get_security_amount;
  const balanceUnit =
    exchangeOrder?.direction === Direction.Buy
      ? exchangeOrder.securityDetails?.currency.currency
      : intl.formatMessage({ id: 'exchangeOrderFormPage.form.pcs' });
  const summary = (
    <>
      {exchangeOrder?.direction && exchangeOrder?.account ? (
        <DataRow
          label={intl.formatMessage({ id: 'exchangeOrderFormPage.form.balanceLabel' })}
          value={formatNumber(
            exchangeOrder.security ? observedBalance?.balance : undefined,
            balanceUnit,
          )}
        />
      ) : null}
      {exchangeOrder?.direction && exchangeOrder?.account ? (
        <DataRow
          label={intl.formatMessage({ id: 'exchangeOrderFormPage.form.investableBalanceLabel' })}
          value={formatNumber(
            exchangeOrder.security ? observedBalance?.investable : undefined,
            balanceUnit,
          )}
          valueClassName="text-success"
        />
      ) : null}
    </>
  );

  const securitySelect = (
    <StyledReactSelect
      placeholder={intl.formatMessage({
        id: 'exchangeOrderFormPage.form.securitySelectPlaceholder',
      })}
      options={
        getSecuritiesQueryResult.data?.get_securities.map((s) => ({
          value: s.short_name,
          label: s.name + ` (${s.currency.currency})`,
        })) || []
      }
      searchable
      onChange={handleSecurityChange}
      isLoading={getSecuritiesQueryResult.fetching}
      defaultValue={
        exchangeOrder?.securityDetails
          ? {
              value: exchangeOrder.securityDetails.short_name,
              label: exchangeOrder?.securityDetails.name,
            }
          : undefined
      }
      value={
        exchangeOrder?.securityDetails
          ? {
              value: exchangeOrder.securityDetails.short_name,
              label: exchangeOrder.securityDetails.name,
            }
          : null
      }
      disabled={!exchangeOrder?.direction || !exchangeOrder?.account}
      error={validationErrors.security && intl.formatMessage({ id: validationErrors.security })}
    />
  );

  const stockExchangeLocality =
    exchangeOrder?.securityDetails !== undefined ? (
      <DataRow
        label={intl.formatMessage({ id: 'exchangeOrderFormPage.form.stockExchangeLocalityLabel' })}
        value={exchangeOrder.securityDetails.stock_market_locality}
      />
    ) : null;

  const daytradeCheckbox = (
    <>
      <div className="flex flex-row items-center justify-between">
        <StyledCheckbox
          onChange={handleDaytradeChange}
          checked={!!exchangeOrder?.daytrade}
          inactive={!exchangeOrder?.direction}
        >
          <FormattedMessage id="exchangeOrderFormPage.form.dayTradeLabel" />
        </StyledCheckbox>
        <InfoIcon
          src={assets.icons.info_svg}
          alt="info"
          onClick={() =>
            openInfo === 'daytrade' ? setOpenInfo(undefined) : setOpenInfo('daytrade')
          }
        />
      </div>
      <ExchangeOrderFieldInfo visible={openInfo === 'daytrade'} yOffset={-24}>
        <p>
          <b>
            <FormattedMessage id="exchangeOrderFormPage.form.dayTradeInfoLabel" />
          </b>{' '}
          <FormattedMessage id="exchangeOrderFormPage.form.dayTradeInfoText" />
        </p>
      </ExchangeOrderFieldInfo>
    </>
  );

  const daytradeTypeSelect = exchangeOrder?.daytrade ? (
    <>
      <div className="text-16 mb-3 mt-3">
        <b>
          <FormattedMessage id="exchangeOrderFormPage.form.dayTradeTypeLabel" />
        </b>
      </div>
      <ButtonSelect
        options={[
          {
            label: intl.formatMessage({ id: 'order.dayTradeType.open' }),
            value: DaytradeType.Open,
          },
          {
            label: intl.formatMessage({ id: 'order.dayTradeType.close' }),
            value: DaytradeType.Close,
          },
        ]}
        onSelect={handleDaytradeTypeChange}
        initialValue={exchangeOrder?.daytrade_type}
        className="mb-3"
        error={validationErrors.daytradeType && intl.formatMessage({ id: validationErrors.daytradeType })}
      />
    </>
  ) : null;

  const exchangeTypeSelect = (
    <>
      <div className="flex flex-row items-center justify-between mb-3">
        <div className="text-16 ">
          <b>
            <FormattedMessage id="exchangeOrderFormPage.form.exchangeTypeLabel" />
          </b>
        </div>
        <InfoIcon
          src={assets.icons.info_svg}
          alt="info"
          onClick={() => (openInfo === 'type' ? setOpenInfo(undefined) : setOpenInfo('type'))}
        />
      </div>
      <ExchangeOrderFieldInfo visible={openInfo === 'type'}>
        <p
          dangerouslySetInnerHTML={{
            __html:
              intl.formatMessage({
                id: 'exchangeOrderFormPage.form.exchangeTypeInfoText',
              }) ?? '',
          }}
        />
      </ExchangeOrderFieldInfo>

      <ButtonSelect
        className="mb-3"
        options={getExchangeTypeOptions(
          intl,
          exchangeOrder?.direction,
          exchangeOrder?.securityDetails,
        )}
        initialValue={exchangeOrder?.type}
        onSelect={handleExchangeTypeChange}
        inactive={!exchangeOrder?.direction}
        error={validationErrors.exchangeType && intl.formatMessage({ id: validationErrors.exchangeType })}
      />
    </>
  );

  const activationPriceInput =
    exchangeOrder?.type === ExchangeRateType.StopLimit ||
    exchangeOrder?.type === ExchangeRateType.StopMarket ? (
      <StyledInput
        style={{ maxWidth: '100%' }}
        type="number"
        placeholder={
          intl.formatMessage({ id: 'exchangeOrderFormPage.form.activationPriceLabel' }) +
          (exchangeOrder.securityDetails?.currency.currency
            ? `(${exchangeOrder.securityDetails?.currency.currency})`
            : '')
        }
        onChange={handleActivationPriceChange}
        disabled={!exchangeOrder?.direction}
        error={validationErrors.activationPrice && intl.formatMessage({ id: validationErrors.activationPrice })}
        value={exchangeOrder?.activation_price?.toString()}
        uncontrolled
        min={0}
      />
    ) : null;

  const limitPriceInput =
    exchangeOrder?.type === ExchangeRateType.StopLimit ||
    exchangeOrder?.type === ExchangeRateType.LimitPrice ? (
      <StyledInput
        style={{ maxWidth: '100%' }}
        type="number"
        placeholder={
          intl.formatMessage({ id: 'exchangeOrderFormPage.form.limitPriceLabel' }) +
          (exchangeOrder.securityDetails?.currency.currency
            ? `(${exchangeOrder.securityDetails?.currency.currency})`
            : '')
        }
        onChange={handleLimitPriceChange}
        error={validationErrors.limitPrice && intl.formatMessage({ id: validationErrors.limitPrice })}
        disabled={!exchangeOrder?.direction}
        value={exchangeOrder?.limit_price?.toString()}
        uncontrolled
        min={0}
      />
    ) : null;

  const countInput = (
    <StyledInput
      style={{ maxWidth: '100%' }}
      type="number"
      placeholder={intl.formatMessage({ id: 'exchangeOrderFormPage.form.countLabel' })}
      onChange={handleCountChange}
      error={validationErrors.count && intl.formatMessage({ id: validationErrors.count })}
      disabled={!exchangeOrder?.direction}
      value={exchangeOrder?.count?.toString()}
      min={0}
    />
  );

  const outsideTradingHoursCheckbox = (
    <>
      <div className="flex flex-row items-center justify-between">
        <StyledCheckbox
          onChange={handleOutsideTradingHoursChange}
          checked={!!exchangeOrder?.outside_trading_hours}
          inactive={!exchangeOrder?.direction}
        >
          <FormattedMessage id="exchangeOrderFormPage.form.outsideTradingHoursLabel" />
        </StyledCheckbox>
        <InfoIcon
          src={assets.icons.info_svg}
          alt="info"
          onClick={() =>
            openInfo === 'stock-market-time'
              ? setOpenInfo(undefined)
              : setOpenInfo('stock-market-time')
          }
        />
      </div>
      <ExchangeOrderFieldInfo visible={openInfo === 'stock-market-time'} yOffset={-24}>
        <p
          dangerouslySetInnerHTML={{
            __html: intl.formatMessage({
              id: 'exchangeOrderFormPage.form.outsideTradingHoursInfoText',
            }),
          }}
        />
      </ExchangeOrderFieldInfo>
    </>
  );

  const datePicker = (
    <DateRangePicker
      startDate={exchangeOrder?.validity_start ? moment(exchangeOrder?.validity_start) : null}
      startDateId="your_unique_start_date_id"
      endDate={exchangeOrder?.validity_end ? moment(exchangeOrder?.validity_end) : null}
      endDateId="your_unique_end_date_id"
      onDatesChange={handleDatesChange}
      focusedInput={datePickerFocusedInput}
      onFocusChange={(focusedInput) => setDatePickerFocusedInput(focusedInput)}
      small
      hideKeyboardShortcutsPanel
      daySize={30}
      minimumNights={0}
      startDatePlaceholderText={intl.formatMessage({
        id: 'exchangeOrderFormPage.form.dateRangeStartLabel',
      })}
      endDatePlaceholderText={intl.formatMessage({
        id: 'exchangeOrderFormPage.form.dateRangeEndLabel',
      })}
      disabled={!exchangeOrder?.direction || exchangeOrder?.daytrade}
      isDayBlocked={(day: moment.Moment) => {
        return !isDateInDates(day.toISOString(), selectableDays);
      }}
      orientation={isMobile ? 'vertical' : 'horizontal'}
    />
  );

  const validityDatePicker = (
    <>
      <div className="text-16 mb-3 mt-3">
        <b>
          <FormattedMessage id="exchangeOrderFormPage.form.validityLabel" />
        </b>
      </div>
      <ButtonSelect
        options={[
          {
            label: intl.formatMessage({ id: 'exchangeOrderFormPage.form.validityDaily' }),
            value: String(true),
          },
          {
            label: intl.formatMessage({ id: 'exchangeOrderFormPage.form.validityUntilCancel' }),
            value: String(false),
          },
        ]}
        onSelect={(e) =>
          handleDatesChange({
            startDate: moment(),
            endDate: e.value === String(true) ? moment() : null,
          })
        }
        initialValue={
          exchangeOrder?.validity_start ? String(!!exchangeOrder?.validity_end) : undefined
        }
        className="mb-3"
        inactive={exchangeOrder?.daytrade || !exchangeOrder?.direction}
        error={validationErrors.dates && intl.formatMessage({ id: validationErrors.dates })}
      />
    </>
  );

  /*const validityDatePicker = (
    <>
      <div className="flex flex-row items-center justify-between mb-3">
        <div className="text-16 ">
          <b>Érvényesség</b>
        </div>
        <InfoIcon
          src={assets.icons.info_svg}
          alt="info"
          onClick={() =>
            openInfo === 'validity' ? setOpenInfo(undefined) : setOpenInfo('validity')
          }
        />
      </div>
      <ExchangeOrderFieldInfo visible={openInfo === 'validity'}>
        <p>
          <b>Érvényesség:</b> A megbízás érvényességi ideje maximum 30 nap lehet.
        </p>
      </ExchangeOrderFieldInfo>
      <div id="validity-form-input" className="w-1/1">
        {datePicker}
      </div>
      <ErrorShower style={{ marginTop: -10 }} error={intl.formatMessage({ id: validationErrors.dates })} />
    </>
  );*/

  const navigationButtons = (
    <div className="flex flex-row">
      <Button secondary onClick={() => history.push(Paths.exchange_orders)} className="mr-3">
        <FormattedMessage id="exchangeOrderFormPage.form.cancelButton" />
      </Button>
      <Button
        onClick={handleNext1Click}
        inactive={!!getSecuritiesQueryResult.error}
        loading={getSecuritiesQueryResult.fetching}
      >
        <FormattedMessage id="exchangeOrderFormPage.form.nextButton" />
      </Button>
    </div>
  );

  return (
    <BlurredContainerM3030 className="mb-56">
      <FormContainer>
        <h2 className="mb-6">
          <FormattedMessage id="exchangeOrderFormPage.form.orderDataLabel" />
        </h2>
        <FormBlockContainer>
          {directionSelect}
          {accountSelect}
          {securitySelect}
          {stockExchangeLocality}
          {summary}
        </FormBlockContainer>
        {/*<FormBlockContainer>
          {daytradeCheckbox}
          {daytradeTypeSelect}
        </FormBlockContainer>*/}
        <FormBlockContainer>
          {exchangeTypeSelect}
          {activationPriceInput}
          {limitPriceInput}
          {countInput}
          {outsideTradingHoursCheckbox}
        </FormBlockContainer>
        <FormBlockContainer>{validityDatePicker}</FormBlockContainer>
      </FormContainer>
      {navigationButtons}
    </BlurredContainerM3030>
  );
};

export default NewExchangeOrderForm;
